import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Check, LayoutGrid, List, Pencil, Plus, Search, Sparkles, Users } from "lucide-react";
import { api, formatDate } from "../api";
import { useApi } from "../hooks";
import { ErrorState, LoadingState, PageHeader } from "../components/Ui";
import Markdown from "../components/Markdown";
import Dialog from "../components/Dialog";
import { plainTextToMarkdown, readableMarkdown } from "../markdown-text";
import { calmHeadings, slugger, splitCover } from "../lesson-doc";
import { setLeaveGuard } from "../leave-guard";
import { roleLabel } from "../roles";
import logoUrl from "../assets/nlm-logo.webp";

// Training on Canon's model: a library of courses, a course page with modules and
// lessons, and a lesson page with its quiz. Views live in the URL:
//   /training                 library
//   /training?course=<slug>   course
//   /training?lesson=<id>     lesson   (&edit=1 authoring)
//   /training?view=team       team progress (authors)

type Kind = "reading" | "kb" | "video";
type State = "complete" | "started" | "not_started";
type CourseSummary = { id: number; title: string; slug: string; description: string | null; module_count: number; total: number; done: number; pct: number; minutes: number; has_started: boolean };
type Library = { courses: CourseSummary[]; continue: { lesson_id: number; lesson_title: string; course_slug: string; course_title: string; module_title: string } | null; can_author: boolean };
type LessonRow = { id: number; title: string; kind: Kind; estimated_minutes: number | null; has_quiz: boolean; state: State };
type Progress = { done: number; total: number; pct: number };
type Course = { id: number; title: string; slug: string; description: string | null; progress: Progress; next: { id: number; title: string } | null; has_started?: boolean; modules: { id: number; title: string; description: string | null; progress: Progress; lessons: LessonRow[] }[]; can_author: boolean };
type QuizOption = { id: string; text: string };
type QuizQuestion = { id: string; prompt: string; options: QuizOption[]; correct_option_id?: string; explanation?: string | null };
type Quiz = { questions: QuizQuestion[] } | null;
/** Why a knowledge base lesson has no document to show: none by that code, no text yet, or no longer in use. */
type KbState = "ok" | "missing" | "empty" | "retired";
type Lesson = {
  id: number; title: string; kind: Kind; body: string | null; kb_code: string | null; kb: { doc_code: string; title: string; full_text: string } | null; kb_state?: KbState | null;
  embed_url: string | null; embed_src: string | null; estimated_minutes: number | null; updated_at: string; quiz: Quiz;
  module: { id: number; title: string }; course: { id: number; title: string; slug: string };
  position: { index: number; total: number }; prev: { id: number; title: string } | null; next: { id: number; title: string } | null;
  progress: { completed: boolean; quiz_best: number | null; quiz_last: number | null; quiz_attempts: number };
  can_author: boolean;
};

const KIND_CHIP: Record<Kind, string> = { reading: "Reading", kb: "Knowledge base", video: "Video" };
// Non-breaking spaces: "5 h 17 min" never breaks inside itself.
const NB = "\u00a0";
const minutes = (m: number | null | undefined) => (m ? (m >= 60 ? `${Math.floor(m / 60)}${NB}h${m % 60 ? `${NB}${m % 60}${NB}min` : ""}` : `${m}${NB}min`) : null);

const KB_STATE_NOTE: Record<Exclude<KbState, "ok">, string> = {
  missing: "This knowledge base document could not be found.",
  empty: "This knowledge base document has no text yet.",
  retired: "This knowledge base document is no longer in use.",
};

// Course covers: a quiet tile with the NL mark. The title is printed beside or under it, never on it.
function Cover({ size = "sm" }: { size?: "sm" | "lg" }) {
  return (
    <span className={`tr-cover ${size}`} aria-hidden="true">
      <img src={logoUrl} alt="" />
    </span>
  );
}

function ProgressRing({ pct, size = 40, label }: { pct: number; size?: number; label?: string }) {
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  return (
    <span className="tr-ring" style={{ width: size, height: size }} role="img" aria-label={label || `${pct}% complete`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} className="track" />
        <circle cx={size / 2} cy={size / 2} r={r} className="fill" strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100} />
      </svg>
      <span>{pct}%</span>
    </span>
  );
}

function StateMark({ state }: { state: State }) {
  return (
    <span className={`tr-state ${state}`} aria-hidden="true">
      {state === "complete" ? <Check /> : <i />}
    </span>
  );
}

export default function Training() {
  const [params] = useSearchParams();
  const lesson = params.get("lesson");
  const course = params.get("course");
  if (params.get("view") === "team") return <TeamProgress />;
  if (lesson) return <LessonView key={`${lesson}-${params.get("edit")}`} id={lesson} edit={params.get("edit") === "1"} />;
  if (course) return <CourseView key={course} slug={course} />;
  return <LibraryView />;
}

// ── LIBRARY ─────────────────────────────────────────────────

type Filter = "all" | "progress" | "done";
function LibraryView() {
  const lib = useApi<Library>("/api/learn/courses");
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"list" | "gallery">(() => (localStorageGet("tr-view") === "gallery" ? "gallery" : "list"));
  const [adding, setAdding] = useState(false);
  useEffect(() => localStorageSet("tr-view", view), [view]);
  const courses = lib.data?.courses || [];
  const visible = courses.filter((c) => {
    const matches = filter === "all" || (filter === "done" ? c.total > 0 && c.done === c.total : (c.done > 0 || c.has_started) && c.done < c.total);
    return matches && `${c.title} ${c.description || ""}`.toLowerCase().includes(query.trim().toLowerCase());
  });
  return (
    <div className="tr-page">
      <PageHeader
        eyebrow="NOLA Academy"
        title="Training"
        description="How No Limits works, taught course by course. Read, watch, and check what stuck."
        actions={lib.data?.can_author ? (
          <div className="workflow-actions">
            <Link className="button secondary" to="/training?view=team"><Users aria-hidden="true" />Team progress</Link>
            <button className="button" onClick={() => setAdding(true)}>New course</button>
          </div>
        ) : undefined}
      />
      {lib.loading && !lib.data ? <LoadingState label="Loading courses" /> : lib.error ? <ErrorState message={lib.error} onRetry={lib.reload} /> : (
        <>
          <BrandBand courses={courses} resume={lib.data?.continue || null} />
          {!courses.length ? (
            <section className="tr-empty">
              <BookOpen aria-hidden="true" />
              <h2>No courses yet</h2>
              <p>{lib.data?.can_author ? "Add a course, then fill its modules with lessons, videos and quizzes." : "Courses your team publishes will appear here."}</p>
              {lib.data?.can_author && <button className="button" onClick={() => setAdding(true)}>Add the first course</button>}
            </section>
          ) : (
            <section aria-label="Courses">
              <div className="tr-toolbar">
                <div className="tr-tabs" role="group" aria-label="Filter courses">
                  {([["all", "All courses"], ["progress", "In progress"], ["done", "Completed"]] as const).map(([k, l]) => (
                    <button key={k} type="button" aria-pressed={filter === k} className={filter === k ? "active" : ""} onClick={() => setFilter(k)}>
                      {l}{k === "all" && <span>{courses.length}</span>}
                    </button>
                  ))}
                </div>
              </div>
              <div className="tr-searchrow">
                <label className="search-control tr-search">
                  <Search aria-hidden="true" />
                  <input type="search" aria-label="Find a course" placeholder="Find a course…" value={query} onChange={(e) => setQuery(e.target.value)} />
                </label>
                <span className="muted" aria-live="polite">{visible.length} {visible.length === 1 ? "course" : "courses"}</span>
                <div className="tr-views" role="group" aria-label="Course view">
                  <button type="button" aria-pressed={view === "list"} aria-label="List view" title="List view" onClick={() => setView("list")}><List /></button>
                  <button type="button" aria-pressed={view === "gallery"} aria-label="Gallery view" title="Gallery view" onClick={() => setView("gallery")}><LayoutGrid /></button>
                </div>
              </div>
              {visible.length ? (
                <ul className={view === "gallery" ? "tr-gallery" : "tr-list"}>
                  {visible.map((c) => (
                    <li key={c.id}>
                      <Link className={view === "gallery" ? "tr-card" : "tr-row"} to={`/training?course=${c.slug}`}>
                        <Cover size={view === "gallery" ? "lg" : "sm"} />
                        <span className="tr-row-copy">
                          <strong>{c.title}</strong>
                          {c.description && <span className="tr-desc">{c.description}</span>}
                          {/* Each part keeps to one line; the line breaks between parts, never inside "5 h 17 min". */}
                          <span className="tr-meta">
                            <span>{c.module_count} {c.module_count === 1 ? "module" : "modules"}</span>
                            <span>{c.done} of {c.total} lessons</span>
                            {minutes(c.minutes) && <span>{minutes(c.minutes)}</span>}
                          </span>
                        </span>
                        <ProgressRing pct={c.pct} size={view === "gallery" ? 44 : 38} label={`${c.title}: ${c.pct}% complete`} />
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="tr-none">
                  <p>{query ? "No courses match your search." : filter === "done" ? "Completed courses will appear here." : "Courses you have started will appear here."}</p>
                  <button className="button secondary" onClick={() => { setQuery(""); setFilter("all"); }}>Show all courses</button>
                </div>
              )}
            </section>
          )}
        </>
      )}
      {adding && <NewCourse onClose={() => setAdding(false)} onSaved={lib.reload} />}
    </div>
  );
}

/**
 * Where to pick up, above the course list. With one course its row already shows the
 * progress, so the band is only the "Continue where you left off" link; the overall
 * count and ring appear once there are two or more courses to add up.
 */
function BrandBand({ courses, resume }: { courses: CourseSummary[]; resume: Library["continue"] }) {
  const several = courses.length > 1;
  if (!resume && !several) return null;
  const total = courses.reduce((n, c) => n + c.total, 0);
  const done = courses.reduce((n, c) => n + c.done, 0);
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <section className={several ? "tr-band" : "tr-band solo"} aria-label="Your training">
      {several && (
        <div className="tr-band-copy">
          <p className="tr-band-word">All courses</p>
          <p className="tr-band-stats">{done} of {total} lessons done</p>
        </div>
      )}
      {resume ? (
        <Link className="tr-band-resume" to={`/training?lesson=${resume.lesson_id}`}>
          <span>Continue where you left off</span>
          <strong>{resume.lesson_title}</strong>
          <small>{several ? `${resume.course_title}, ${resume.module_title}` : resume.module_title}</small>
        </Link>
      ) : (
        <div className="tr-band-resume idle"><span>Your progress</span><strong>{done ? `${pct}% complete` : "Pick a course to start"}</strong></div>
      )}
      {several && <ProgressRing pct={pct} size={56} label={`${pct}% of all training complete`} />}
    </section>
  );
}

function localStorageGet(key: string) { try { return window.localStorage.getItem(key); } catch { return null; } }
function localStorageSet(key: string, value: string) { try { window.localStorage.setItem(key, value); } catch { /* private mode */ } }

function NewCourse({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [, setParams] = useSearchParams();
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const c = await api<{ slug: string }>("/api/learn/courses", { method: "POST", body: JSON.stringify({ title, description }) });
      onSaved(); onClose();
      setParams({ course: c.slug });
    } catch (err) { setError(err instanceof Error ? err.message : "Could not create the course"); } finally { setBusy(false); }
  }
  return (
    <Dialog title="New course" onClose={onClose}>
      <form className="operation-form" onSubmit={(e) => void save(e)}>
        <label>Title<input value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus /></label>
        <label>What it covers<textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></label>
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="button secondary" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="button" disabled={busy || !title.trim()}>{busy ? "Creating…" : "Create course"}</button>
        </div>
      </form>
    </Dialog>
  );
}

// ── COURSE ──────────────────────────────────────────────────

/** The type most of a course's lessons share. Its tag says nothing on every row, so only the others carry one. */
function commonKind(lessons: LessonRow[]): Kind | null {
  const counts = new Map<Kind, number>();
  for (const l of lessons) counts.set(l.kind, (counts.get(l.kind) || 0) + 1);
  for (const [kind, n] of counts) if (n * 2 > lessons.length) return kind;
  return null;
}

function CourseView({ slug }: { slug: string }) {
  const course = useApi<Course>(`/api/learn/courses/${encodeURIComponent(slug)}`);
  const [editing, setEditing] = useState(false);
  const [addingModule, setAddingModule] = useState(false);
  const [editingModule, setEditingModule] = useState<Course["modules"][number] | null>(null);
  if (course.loading && !course.data) return <LoadingState label="Loading course" />;
  if (course.error || !course.data) return <ErrorState message={course.error || "Course not found"} onRetry={course.reload} />;
  const c = course.data;
  const lessonCount = c.progress.total;
  const common = commonKind(c.modules.flatMap((m) => m.lessons));
  return (
    <div className="tr-page tr-doc">
      {/* No crumb line here: the top bar already reads "Agency > Training", and the Training tab above goes back to all courses. */}
      <header className="tr-course-head">
        <div>
          <span className="tr-symbol" aria-hidden="true"><img src={logoUrl} alt="" /></span>
          <h1>{c.title}</h1>
          {c.description && <p className="tr-lede">{c.description}</p>}
          <p className="tr-count">{c.progress.done} of {lessonCount} lessons complete</p>
          {c.next ? (
            // "Continue" once any lesson has been opened: the same lesson the library offers to resume.
            <Link className="button tr-continue-btn" to={`/training?lesson=${c.next.id}`}>{c.has_started || c.progress.done ? "Continue" : "Start"}: {c.next.title}</Link>
          ) : (
            // Green is for a finished course only; an empty one is not a success.
            lessonCount ? <p className="tr-finished">Course complete</p> : <p className="tr-count">No lessons yet</p>
          )}
        </div>
        <div className="tr-course-side">
          <ProgressRing pct={c.progress.pct} size={76} />
          {c.can_author && <button className="button secondary" onClick={() => setEditing(true)}><Pencil aria-hidden="true" />Edit course</button>}
        </div>
      </header>
      <div className="tr-modules">
        {c.modules.map((m, i) => (
          <section key={m.id} className="tr-module">
            <header>
              <div>
                <span className="tr-module-num">Module {i + 1}</span>
                <h2>{m.title}</h2>
                {m.description && <p className="tr-module-desc">{m.description}</p>}
              </div>
              <span className="tr-module-side">
                {c.can_author && <button type="button" className="text-button" onClick={() => setEditingModule(m)} aria-label={`Edit module ${i + 1}`}>Edit</button>}
                <span className="tr-module-count">{m.progress.done}/{m.progress.total}</span>
              </span>
            </header>
            {m.lessons.length ? (
              <ul>
                {m.lessons.map((l) => (
                  <li key={l.id}>
                    <Link className="tr-lesson-row" to={`/training?lesson=${l.id}`}>
                      <StateMark state={l.state} />
                      <span className="sr-only">{l.state === "complete" ? "Completed" : l.state === "started" ? "In progress" : "Not started"}</span>
                      <span className="tr-lesson-title">{l.title}</span>
                      {l.kind !== "reading" && l.kind !== common && <span className="tr-chip">{KIND_CHIP[l.kind]}</span>}
                      {l.has_quiz && <span className="tr-chip">Quiz</span>}
                      {/* A fixed-width column, so the chips beside it line up whatever the duration. */}
                      <span className="tr-min">{minutes(l.estimated_minutes)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : <p className="tr-module-empty">No lessons in this module yet.</p>}
            {c.can_author && <AddLesson moduleId={m.id} onAdded={course.reload} />}
          </section>
        ))}
        {!c.modules.length && <section className="tr-empty"><p>This course has no modules yet.</p></section>}
        {c.can_author && (addingModule ? (
          <AddModule courseId={c.id} onClose={() => setAddingModule(false)} onAdded={() => { setAddingModule(false); course.reload(); }} />
        ) : (
          <button className="tr-add-module" onClick={() => setAddingModule(true)}>+ Add module</button>
        ))}
      </div>
      {editing && <EditCourse course={c} onClose={() => setEditing(false)} onSaved={course.reload} />}
      {editingModule && <EditModule module={editingModule} onClose={() => setEditingModule(null)} onSaved={course.reload} />}
    </div>
  );
}

function AddLesson({ moduleId, onAdded }: { moduleId: number; onAdded: () => void }) {
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<Kind>("reading");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [, setParams] = useSearchParams();
  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const l = await api<{ id: number }>(`/api/learn/modules/${moduleId}/lessons`, { method: "POST", body: JSON.stringify({ title, kind }) });
      setTitle(""); onAdded();
      setParams({ lesson: String(l.id), edit: "1" });
    } catch (err) { setError(err instanceof Error ? err.message : "Could not add the lesson"); } finally { setBusy(false); }
  }
  return (
    <details className="tr-add">
      <summary><Plus aria-hidden="true" />Add lesson</summary>
      <form className="tr-add-form" onSubmit={(e) => void add(e)}>
        <input aria-label="Lesson title" placeholder="Lesson title" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <select aria-label="Lesson type" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
          <option value="reading">Reading</option>
          <option value="kb">Knowledge base document</option>
          <option value="video">Video</option>
        </select>
        <button className="button" disabled={busy || !title.trim()}>Add and edit</button>
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
      </form>
    </details>
  );
}

function AddModule({ courseId, onClose, onAdded }: { courseId: number; onClose: () => void; onAdded: () => void }) {
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");
  async function add(e: FormEvent) {
    e.preventDefault();
    try { await api(`/api/learn/courses/${courseId}/modules`, { method: "POST", body: JSON.stringify({ title }) }); onAdded(); }
    catch (err) { setError(err instanceof Error ? err.message : "Could not add the module"); }
  }
  return (
    <form className="tr-module tr-add-form tr-new-module" onSubmit={(e) => void add(e)}>
      <input aria-label="Module title" placeholder="Module title" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
      <button className="button" disabled={!title.trim()}>Add module</button>
      <button type="button" className="button secondary" onClick={onClose}>Cancel</button>
      {error && <p className="inline-notice danger" role="alert">{error}</p>}
    </form>
  );
}

function EditCourse({ course, onClose, onSaved }: { course: Course; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(course.title);
  const [description, setDescription] = useState(course.description || "");
  const [error, setError] = useState("");
  const [, setParams] = useSearchParams();
  async function save(body: Record<string, unknown>) {
    try { await api(`/api/learn/courses/${course.id}`, { method: "PUT", body: JSON.stringify(body) }); onSaved(); onClose(); return true; }
    catch (err) { setError(err instanceof Error ? err.message : "Could not save"); return false; }
  }
  return (
    <Dialog title="Edit course" onClose={onClose}>
      <form className="operation-form" onSubmit={(e) => { e.preventDefault(); void save({ title, description }); }}>
        <label>Title<input value={title} onChange={(e) => setTitle(e.target.value)} required /></label>
        <label>What it covers<textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></label>
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="text-button tr-danger tr-hide-action" onClick={() => { if (window.confirm("Hide this course from the team? Progress is kept and it can be restored.")) void save({ active: false }).then((ok) => ok && setParams({})); }}>Hide course</button>
          <button type="button" className="button secondary" onClick={onClose}>Cancel</button>
          <button className="button" disabled={!title.trim()}>Save</button>
        </div>
      </form>
    </Dialog>
  );
}

/** A module's title and the line under it; hiding keeps its lessons and everyone's progress. */
function EditModule({ module, onClose, onSaved }: { module: Course["modules"][number]; onClose: () => void; onSaved: () => void }) {
  const [title, setTitle] = useState(module.title);
  const [description, setDescription] = useState(module.description || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function save(body: Record<string, unknown>) {
    setBusy(true);
    try { await api(`/api/learn/modules/${module.id}`, { method: "PUT", body: JSON.stringify(body) }); onSaved(); onClose(); }
    catch (err) { setError(err instanceof Error ? err.message : "Could not save the module"); }
    finally { setBusy(false); }
  }
  return (
    <Dialog title="Edit module" onClose={() => { if (!busy) onClose(); }}>
      <form className="operation-form" onSubmit={(e) => { e.preventDefault(); void save({ title: title.trim(), description: description.trim() }); }}>
        <label>Title<input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} /></label>
        <label>What it covers<textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} /></label>
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        <div className="operation-actions">
          <button type="button" className="text-button tr-danger tr-hide-action" disabled={busy} onClick={() => { if (window.confirm("Hide this module from the team? Its lessons and everyone's progress are kept.")) void save({ active: false }); }}>Hide module</button>
          <button type="button" className="button secondary" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="button" disabled={busy || !title.trim()}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Dialog>
  );
}

// ── LESSON ──────────────────────────────────────────────────

function LessonView({ id, edit }: { id: string; edit: boolean }) {
  const lesson = useApi<Lesson>(`/api/learn/lessons/${id}${edit ? "?edit=1" : ""}`);
  const [, setParams] = useSearchParams();
  const sheet = useRef<HTMLElement>(null);
  if (lesson.loading && !lesson.data) return <LoadingState label="Loading lesson" />;
  if (lesson.error || !lesson.data) return <ErrorState message={lesson.error || "Lesson not found"} onRetry={lesson.reload} />;
  const l = lesson.data;
  return (
    <div className="tr-lesson">
      {/* One way back to the course, not a second breadcrumb: the top bar already shows "Agency > Training". */}
      <p className="tr-crumbs tr-back">
        <Link to={`/training?course=${l.course.slug}`} aria-label={`Back to ${l.course.title}`}><ArrowLeft aria-hidden="true" />{l.course.title}</Link>
      </p>
      <div className={`tr-lesson-grid${edit ? " editing" : ""}`}>
        {/* The lesson reads like a document: one white sheet, a measured column, the outline beside it. */}
        <article className="tr-sheet" ref={sheet}>
          <header className="tr-sheet-head">
            <p className="tr-lesson-pos">
              <span className="tr-lesson-module">{l.module.title}</span>
              <span>Lesson {l.position.index} of {l.position.total}</span>
              {minutes(l.estimated_minutes) && <span>{minutes(l.estimated_minutes)}</span>}
            </p>
            {edit ? <h1>Edit lesson</h1> : (
              <div className="tr-lesson-titlebar">
                <h1>{l.title}</h1>
                {l.can_author && <button className="button secondary" onClick={() => setParams({ lesson: String(l.id), edit: "1" })}><Pencil aria-hidden="true" />Edit</button>}
              </div>
            )}
            {!edit && l.kind === "kb" && l.kb && (
              <p className="tr-source"><BookOpen aria-hidden="true" />From the knowledge base <Link to={`/knowledge/${l.kb.doc_code}`}><span className="tr-source-code">{l.kb.doc_code}</span>{l.kb.title}</Link></p>
            )}
          </header>
          {edit ? (
            <LessonEditor lesson={l} onSaved={() => setParams({ lesson: String(l.id) })} onCancel={() => setParams({ lesson: String(l.id) })} />
          ) : (
            <>
              <LessonBody lesson={l} />
              {l.quiz && <QuizBlock lesson={l} onResult={(completed) => completed && lesson.setData({ ...l, progress: { ...l.progress, completed: true } })} />}
              <LessonActions lesson={l} onChange={(completed) => lesson.setData({ ...l, progress: { ...l.progress, completed } })} />
            </>
          )}
        </article>
        {!edit && <aside className="tr-rail" aria-label="Lesson outline"><LessonOutline key={l.id} root={sheet} lesson={l} /></aside>}
      </div>
    </div>
  );
}

/** "On this page": the lesson's headings, with the one you are reading highlighted. */
function LessonOutline({ root, lesson: l }: { root: RefObject<HTMLElement | null>; lesson: Lesson }) {
  const [items, setItems] = useState<{ id: string; text: string; level: number }[]>([]);
  const [active, setActive] = useState("");
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const slug = slugger();
    const heads = [...el.querySelectorAll<HTMLElement>(".tr-doc-body h2, .tr-doc-body h3")];
    heads.forEach((h) => { if (!h.id) h.id = slug(h.textContent || ""); });
    // Long documents list their main sections only; short ones show sub-sections too.
    const all = heads.map((h) => ({ id: h.id, text: h.textContent || "", level: h.tagName === "H3" ? 3 : 2 }));
    const tops = all.filter((it) => it.level === 2);
    setItems(tops.length >= 4 || all.length > 16 ? (tops.length >= 2 ? tops : all.slice(0, 16)) : all);
    if (!heads.length || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver((entries) => {
      const seen = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (seen) setActive(seen.target.id);
    }, { rootMargin: "-80px 0px -65% 0px" });
    heads.forEach((h) => io.observe(h));
    return () => io.disconnect();
  }, [root, l.id]);
  return (
    <div className="tr-outline">
      <p className="tr-outline-pos"><span className="tr-outline-bar" aria-hidden="true"><i style={{ width: `${Math.round((l.position.index / Math.max(1, l.position.total)) * 100)}%` }} /></span>Lesson {l.position.index} of {l.position.total}</p>
      {items.length >= 2 && <>
        <p className="tr-outline-label">On this page</p>
        <ol>{items.map((it) => (
          <li key={it.id} className={`${it.level === 3 ? "sub" : ""}${active === it.id ? " active" : ""}`}>
            <a href={`#${it.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(it.id)?.scrollIntoView({ behavior: "smooth", block: "start" }); history.replaceState(null, "", `${location.pathname}${location.search}#${it.id}`); }}>{it.text}</a>
          </li>
        ))}</ol>
      </>}
      {l.next && <Link className="tr-outline-next" to={`/training?lesson=${l.next.id}`}><span>Up next</span><strong>{l.next.title}</strong></Link>}
    </div>
  );
}

function LessonBody({ lesson: l }: { lesson: Lesson }) {
  if (l.kind === "video") {
    return (
      <div className="tr-video-wrap">
        {l.embed_src ? (
          <div className="tr-video"><iframe src={l.embed_src} title={l.title} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen loading="lazy" referrerPolicy="strict-origin-when-cross-origin" /></div>
        ) : l.embed_url ? (
          <p className="tr-note">This video can't play inside NOLA. <a href={l.embed_url} target="_blank" rel="noreferrer">Open it in a new tab</a>.</p>
        ) : <p className="tr-note">No video has been added to this lesson yet.</p>}
        {l.body && <div className="tr-prose tr-doc-body"><Markdown>{calmHeadings(l.body)}</Markdown></div>}
      </div>
    );
  }
  if (l.kind === "kb") {
    // A missing, empty or retired document: one line saying so (never a blank page), and for
    // authors the way to pick another document.
    if (!l.kb?.full_text?.trim()) {
      const state = l.kb_state && l.kb_state !== "ok" ? l.kb_state : l.kb ? "empty" : "missing";
      return (
        <div className="tr-prose tr-doc-body">
          {l.body && <div className="tr-intro"><Markdown>{calmHeadings(l.body)}</Markdown></div>}
          <p className="tr-note tr-kb-note">
            {KB_STATE_NOTE[state]}
            {l.can_author && <> <Link to={`/training?lesson=${l.id}&edit=1`}>Edit lesson</Link> to choose another document.</>}
          </p>
        </div>
      );
    }
    // KB documents are Word pastes; rebuild headings, lists and tables the way the Knowledge Base does.
    const { cover, body } = splitCover(calmHeadings(plainTextToMarkdown(readableMarkdown(l.kb.full_text))), l.kb.title);
    return (
      <div className="tr-prose tr-doc-body">
        {l.body && <div className="tr-intro"><Markdown>{calmHeadings(l.body)}</Markdown></div>}
        {cover && (
          <div className="tr-cover-block">
            {cover.eyebrow && <p className="tr-cover-eyebrow">{cover.eyebrow}</p>}
            {cover.title && <p className="tr-cover-title">{cover.title}</p>}
            {cover.lines.map((line, i) => <p key={i} className="tr-cover-line">{line}</p>)}
            {cover.meta && <p className="tr-cover-meta">{cover.meta}</p>}
          </div>
        )}
        <Markdown>{body}</Markdown>
      </div>
    );
  }
  return l.body?.trim() ? <div className="tr-prose tr-doc-body"><Markdown>{calmHeadings(l.body)}</Markdown></div> : <p className="tr-note">This lesson has no content yet.</p>;
}

function LessonActions({ lesson: l, onChange }: { lesson: Lesson; onChange: (completed: boolean) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [, setParams] = useSearchParams();
  async function toggle() {
    setBusy(true); setError("");
    const completed = !l.progress.completed;
    try {
      await api(`/api/learn/lessons/${l.id}/complete`, { method: "POST", body: JSON.stringify({ completed }) });
      onChange(completed);
      // Marking done moves you on, the way you'd turn a page.
      if (completed && l.next) setParams({ lesson: String(l.next.id) });
    } catch (err) { setError(err instanceof Error ? err.message : "Could not save"); } finally { setBusy(false); }
  }
  return (
    <div className="tr-actions">
      <button className={l.progress.completed ? "button secondary" : "button"} aria-pressed={l.progress.completed} disabled={busy} onClick={() => void toggle()}>
        {l.progress.completed ? <><Check aria-hidden="true" />Completed</> : l.next ? "Mark complete and continue" : "Mark complete"}
      </button>
      {error && <span className="tr-error" role="alert">{error}</span>}
      <span className="tr-spacer" />
      {l.prev && <Link to={`/training?lesson=${l.prev.id}`} title={l.prev.title}>← Previous</Link>}
      {l.next ? <Link to={`/training?lesson=${l.next.id}`} title={l.next.title}>Next →</Link> : <Link to={`/training?course=${l.course.slug}`}>Back to course</Link>}
    </div>
  );
}

// ── QUIZ ────────────────────────────────────────────────────

type QuizResult = { score: number; correct: number; total: number; passed: boolean; pass_mark: number; best: number; attempts: number; completed: boolean; results: { id: string; chosen: string | null; correct: boolean; correct_option_id: string; explanation: string | null }[] };

function QuizBlock({ lesson: l, onResult }: { lesson: Lesson; onResult: (completed: boolean) => void }) {
  const questions = l.quiz?.questions || [];
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const byId = useMemo(() => new Map((result?.results || []).map((r) => [r.id, r])), [result]);
  const answered = questions.filter((q) => answers[q.id]).length;
  async function check() {
    setBusy(true); setError("");
    try {
      const r = await api<QuizResult>(`/api/learn/lessons/${l.id}/quiz`, { method: "POST", body: JSON.stringify({ answers }) });
      setResult(r); onResult(r.completed);
    } catch (err) { setError(err instanceof Error ? err.message : "Could not check the answers"); } finally { setBusy(false); }
  }
  return (
    <section className="tr-quiz" aria-labelledby={`quiz-${l.id}`}>
      <header>
        <h2 id={`quiz-${l.id}`}>Check your understanding</h2>
        <span>{questions.length} {questions.length === 1 ? "question" : "questions"}{l.progress.quiz_best != null && !result ? `, best so far ${l.progress.quiz_best}%` : ""}</span>
      </header>
      <ol>
        {questions.map((q, i) => {
          const r = byId.get(q.id);
          return (
            <li key={q.id} className={r ? (r.correct ? "right" : "wrong") : ""}>
              <fieldset disabled={!!result}>
                <legend><span>{i + 1}.</span> {q.prompt}</legend>
                {q.options.map((o) => {
                  const isAnswer = r && o.id === r.correct_option_id;
                  const isChosenWrong = r && !r.correct && o.id === r.chosen;
                  return (
                    <label key={o.id} className={`tr-option${answers[q.id] === o.id ? " chosen" : ""}${isAnswer ? " answer" : ""}${isChosenWrong ? " miss" : ""}`}>
                      <input type="radio" name={`q-${q.id}`} value={o.id} checked={answers[q.id] === o.id} onChange={() => setAnswers({ ...answers, [q.id]: o.id })} />
                      <span>{o.text}</span>
                      {isAnswer && <em>Correct answer</em>}
                      {isChosenWrong && <em className="miss">Your answer</em>}
                    </label>
                  );
                })}
                {r?.explanation && <p className="tr-explain">{r.explanation}</p>}
              </fieldset>
            </li>
          );
        })}
      </ol>
      {error && <p className="inline-notice danger" role="alert">{error}</p>}
      {result ? (
        <div className={`tr-quiz-result ${result.passed ? "pass" : "fail"}`} role="status">
          <strong>{result.correct} of {result.total} right ({result.score}%)</strong>
          <span>{result.passed ? (result.completed ? "Passed. This lesson is marked complete." : "Passed.") : `You need ${result.pass_mark}% to pass. Review the lesson and try again.`}</span>
          <button className="button secondary" onClick={() => { setResult(null); setAnswers({}); }}>Try again</button>
        </div>
      ) : (
        <div className="tr-quiz-foot">
          <button className="button" disabled={busy || answered < questions.length} onClick={() => void check()}>{busy ? "Checking…" : "Check answers"}</button>
          <span className="muted">{answered < questions.length ? `${questions.length - answered} left to answer` : "All answered"}</span>
        </div>
      )}
    </section>
  );
}

// ── AUTHORING ───────────────────────────────────────────────

function LessonEditor({ lesson: l, onSaved, onCancel }: { lesson: Lesson; onSaved: () => void; onCancel: () => void }) {
  const [title, setTitle] = useState(l.title);
  const [kind, setKind] = useState<Kind>(l.kind);
  const [body, setBody] = useState(l.body || "");
  const [kbCode, setKbCode] = useState(l.kb_code || "");
  const [embedUrl, setEmbedUrl] = useState(l.embed_url || "");
  const [mins, setMins] = useState(l.estimated_minutes != null ? String(l.estimated_minutes) : "");
  const [quiz, setQuiz] = useState<QuizQuestion[]>(l.quiz?.questions || []);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const kb = useApi<{ docs: { doc_code: string; title: string }[] }>(kind === "kb" ? "/api/learn/kb-options" : null);
  const [, setParams] = useSearchParams();

  // Unsaved changes: anything that differs from what the editor opened with. Leaving then
  // asks first, through the app's links (leave-guard.ts), Cancel, or a reload.
  const opened = useRef(JSON.stringify([l.title, l.kind, l.body || "", l.kb_code || "", l.embed_url || "", l.estimated_minutes != null ? String(l.estimated_minutes) : "", l.quiz?.questions || []]));
  const dirty = JSON.stringify([title, kind, body, kbCode, embedUrl, mins, quiz]) !== opened.current;
  useEffect(() => {
    setLeaveGuard(dirty ? "You have unsaved changes to this lesson. Leave and discard them?" : null);
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  useEffect(() => () => setLeaveGuard(null), []);
  const leave = (go: () => void) => { setLeaveGuard(null); go(); };
  const cancel = () => {
    if (dirty && !window.confirm("Discard your changes to this lesson?")) return;
    leave(onCancel);
  };

  async function save() {
    setBusy(true); setError("");
    try {
      await api(`/api/learn/lessons/${l.id}`, { method: "PUT", body: JSON.stringify({
        base_updated_at: l.updated_at, title, kind, body, kb_code: kind === "kb" ? kbCode : null,
        embed_url: kind === "video" ? embedUrl : null, estimated_minutes: mins === "" ? null : Number(mins),
        quiz: quiz.length ? { questions: quiz } : null,
      }) });
      leave(onSaved);
    } catch (err) { setError(err instanceof Error ? err.message : "Could not save"); } finally { setBusy(false); }
  }
  async function generate() {
    setBusy(true); setError(""); setNotice("");
    try {
      const r = await api<{ quiz: { questions: QuizQuestion[] }; note: string }>(`/api/learn/lessons/${l.id}/quiz/generate`, { method: "POST", body: JSON.stringify({ count: 5 }) });
      setQuiz([...quiz, ...r.quiz.questions]); setNotice(`${r.quiz.questions.length} questions drafted. ${r.note}`);
    } catch (err) { setError(err instanceof Error ? err.message : "Could not generate questions"); } finally { setBusy(false); }
  }
  async function move(direction: "up" | "down") {
    try { await api(`/api/learn/lessons/${l.id}/move`, { method: "POST", body: JSON.stringify({ direction }) }); setNotice(`Moved ${direction}. It takes effect in the course outline.`); }
    catch (err) { setError(err instanceof Error ? err.message : "Could not move"); }
  }
  async function remove() {
    if (!window.confirm(`Remove "${l.title}"? Learners' progress on it is kept, but the lesson disappears from the course.`)) return;
    try { await api(`/api/learn/lessons/${l.id}`, { method: "DELETE" }); leave(() => setParams({ course: l.course.slug })); }
    catch (err) { setError(err instanceof Error ? err.message : "Could not remove"); }
  }
  const preview_src = kind === "video" ? embedSrcClient(embedUrl) : null;
  // The lesson's document may be one that can no longer be picked (retired or empty): it stays
  // in the list, marked, until another is chosen.
  const kbDocs = kb.data?.docs || [];
  const kbCurrentMissing = kind === "kb" && !!kbCode && !!kb.data && !kbDocs.some((d) => d.doc_code === kbCode);
  return (
    <div className="tr-editor">
      <label className="tr-title-input"><span className="sr-only">Lesson title</span><input value={title} onChange={(e) => setTitle(e.target.value)} /></label>
      <div className="tr-editor-row">
        <label>Type
          <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
            <option value="reading">Reading</option><option value="kb">Knowledge base document</option><option value="video">Video</option>
          </select>
        </label>
        <label>Minutes<input type="number" min={0} max={600} value={mins} onChange={(e) => setMins(e.target.value)} /></label>
        {kind === "kb" && (
          <label className="grow">Document
            <select value={kbCode} onChange={(e) => setKbCode(e.target.value)}>
              <option value="">Choose a document</option>
              {kbCurrentMissing && <option value={kbCode}>{kbCode}: no text yet or no longer in use</option>}
              {kbDocs.map((d) => <option key={d.doc_code} value={d.doc_code}>{d.doc_code}: {d.title}</option>)}
            </select>
          </label>
        )}
        {kind === "video" && (
          <label className="grow">Video link<input value={embedUrl} onChange={(e) => setEmbedUrl(e.target.value)} placeholder="YouTube, Vimeo, Loom or Google Drive link" /></label>
        )}
      </div>
      {kind === "video" && embedUrl && (preview_src
        ? <div className="tr-video"><iframe src={preview_src} title="Video preview" allowFullScreen loading="lazy" /></div>
        : <p className="tr-note">That link can't be embedded. Use a YouTube, Vimeo, Loom or Google Drive share link.</p>)}
      <div className="tr-editor-body">
        <div className="tr-editor-bar">
          <span>{kind === "reading" ? "Lesson text" : "Introduction (optional)"}</span>
          <div className="tr-mode" role="group" aria-label="Editor mode">
            <button type="button" aria-pressed={!preview} onClick={() => setPreview(false)}>Write</button>
            <button type="button" aria-pressed={preview} onClick={() => setPreview(true)}>Preview</button>
          </div>
        </div>
        {preview
          ? <div className="tr-prose tr-editor-preview">{body.trim() ? <Markdown>{body}</Markdown> : <p className="muted">Nothing written yet.</p>}</div>
          : <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={kind === "reading" ? 16 : 5} placeholder="Markdown works: ## headings, - lists, **bold**, [links](https://…)" />}
      </div>
      <QuizEditor questions={quiz} onChange={setQuiz} onGenerate={() => void generate()} busy={busy} />
      {notice && <p className="inline-notice success" role="status">{notice}</p>}
      {error && <p className="inline-notice danger" role="alert">{error}</p>}
      <div className="tr-editor-foot">
        <button className="button" disabled={busy || !title.trim()} onClick={() => void save()}>{busy ? "Saving…" : "Save lesson"}</button>
        <button className="button secondary" onClick={cancel}>Cancel</button>
        <span className="tr-spacer" />
        <span className="tr-editor-order">
          <button className="button secondary" onClick={() => void move("up")}>Move up</button>
          <button className="button secondary" onClick={() => void move("down")}>Move down</button>
          <button className="button secondary tr-danger" onClick={() => void remove()}>Remove</button>
        </span>
      </div>
    </div>
  );
}

// Mirrors utils/learning.js embedSrc for the live preview; the server decides what plays.
function embedSrcClient(url: string): string | null {
  let u: URL;
  try { u = new URL(url.trim()); } catch { return null; }
  if (u.protocol !== "https:") return null;
  const host = u.hostname.replace(/^www\./, "");
  if (host === "youtube.com" || host === "m.youtube.com") { const id = u.searchParams.get("v"); return id ? `https://www.youtube-nocookie.com/embed/${id}` : null; }
  if (host === "youtu.be") return `https://www.youtube-nocookie.com/embed/${u.pathname.slice(1)}`;
  if (host === "vimeo.com") { const id = u.pathname.split("/").find((p) => /^\d+$/.test(p)); return id ? `https://player.vimeo.com/video/${id}` : null; }
  if (host === "loom.com") { const id = u.pathname.split("/").filter(Boolean)[1]; return id ? `https://www.loom.com/embed/${id}` : null; }
  if (host === "drive.google.com") { const m = u.pathname.match(/\/file\/d\/([\w-]+)/); return m ? `https://drive.google.com/file/d/${m[1]}/preview` : null; }
  return null;
}

const rid = () => Math.random().toString(36).slice(2, 10);
function QuizEditor({ questions, onChange, onGenerate, busy }: { questions: QuizQuestion[]; onChange: (q: QuizQuestion[]) => void; onGenerate: () => void; busy: boolean }) {
  const set = (i: number, patch: Partial<QuizQuestion>) => onChange(questions.map((q, n) => (n === i ? { ...q, ...patch } : q)));
  const add = () => onChange([...questions, { id: rid(), prompt: "", options: [{ id: rid(), text: "" }, { id: rid(), text: "" }], correct_option_id: undefined, explanation: "" }]);
  return (
    <section className="tr-quiz-editor">
      <header>
        <h2>Quiz</h2>
        <span className="muted">{questions.length ? `${questions.length} ${questions.length === 1 ? "question" : "questions"}. 80% passes and completes the lesson.` : "Optional. Learners pass at 80%."}</span>
        <span className="tr-spacer" />
        <button type="button" className="button secondary" disabled={busy} onClick={onGenerate}><Sparkles aria-hidden="true" />Draft questions with AI</button>
        <button type="button" className="button secondary" onClick={add}>Add question</button>
      </header>
      {questions.map((q, i) => (
        <fieldset key={q.id} className="tr-q">
          <legend>Question {i + 1}</legend>
          <input aria-label={`Question ${i + 1}`} placeholder="Question" value={q.prompt} onChange={(e) => set(i, { prompt: e.target.value })} />
          {q.options.map((o, j) => (
            <div key={o.id} className="tr-q-option">
              <input type="radio" name={`correct-${q.id}`} aria-label={`Mark answer ${j + 1} correct`} checked={q.correct_option_id === o.id} onChange={() => set(i, { correct_option_id: o.id })} />
              <input aria-label={`Answer ${j + 1}`} placeholder={`Answer ${j + 1}`} value={o.text} onChange={(e) => set(i, { options: q.options.map((x) => (x.id === o.id ? { ...x, text: e.target.value } : x)) })} />
              {q.options.length > 2 && <button type="button" className="text-button" aria-label={`Remove answer ${j + 1}`} onClick={() => set(i, { options: q.options.filter((x) => x.id !== o.id), correct_option_id: q.correct_option_id === o.id ? undefined : q.correct_option_id })}>×</button>}
            </div>
          ))}
          <div className="tr-q-tools">
            {q.options.length < 6 && <button type="button" className="text-button" onClick={() => set(i, { options: [...q.options, { id: rid(), text: "" }] })}>+ Answer</button>}
            <span className="muted">{q.correct_option_id ? "Correct answer marked" : "Select the correct answer"}</span>
            <span className="tr-spacer" />
            <button type="button" className="text-button tr-danger" onClick={() => onChange(questions.filter((_, n) => n !== i))}>Remove question</button>
          </div>
          <input aria-label={`Explanation for question ${i + 1}`} placeholder="Why the right answer is right (shown after checking)" value={q.explanation || ""} onChange={(e) => set(i, { explanation: e.target.value })} />
        </fieldset>
      ))}
    </section>
  );
}

// ── TEAM ────────────────────────────────────────────────────

function TeamProgress() {
  const team = useApi<{ rows: { user_id: number; full_name: string; role: string; course_id: number; course_title: string; total: number; done: number; pct: number; avg_quiz: number | null; last_active: string | null }[] }>("/api/learn/team");
  const people = useMemo(() => {
    const m = new Map<number, { name: string; role: string; courses: typeof rows; last: string | null }>();
    const rows = team.data?.rows || [];
    for (const r of rows) {
      if (!m.has(r.user_id)) m.set(r.user_id, { name: r.full_name, role: r.role, courses: [], last: null });
      const p = m.get(r.user_id)!;
      p.courses.push(r);
      if (r.last_active && (!p.last || r.last_active > p.last)) p.last = r.last_active;
    }
    return [...m.values()];
  }, [team.data]);
  const rows = team.data?.rows || [];
  const courses = [...new Map(rows.map((r) => [r.course_id, r.course_title])).entries()];
  return (
    <div className="tr-page">
      <p className="tr-crumbs tr-back"><Link to="/training"><ArrowLeft aria-hidden="true" />All courses</Link></p>
      <PageHeader eyebrow="NOLA Academy" title="Team progress" description="Lessons completed and quiz results for everyone on the team." />
      {team.loading && !team.data ? <LoadingState label="Loading progress" /> : team.error ? <ErrorState message={team.error} onRetry={team.reload} /> : (
        <section className="panel">
          <div className="table-wrap">
            <table className="workflow-table tr-team">
              <thead><tr><th>Person</th>{courses.map(([id, t]) => <th key={id}>{t}</th>)}<th>Last active</th></tr></thead>
              <tbody>
                {people.map((p) => (
                  <tr key={p.name}>
                    <td data-label="Person"><strong>{p.name}</strong><span className="tr-sub">{roleLabel(p.role)}</span></td>
                    {courses.map(([id, t]) => {
                      const c = p.courses.find((x) => x.course_id === id);
                      return (
                        <td key={id} data-label={t}>
                          {c ? <TeamCell done={c.done} total={c.total} pct={c.pct} quiz={c.avg_quiz} /> : <span className="muted">Not started</span>}
                        </td>
                      );
                    })}
                    <td data-label="Last active">{p.last ? formatDate(p.last) : "Not started"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
function TeamCell({ done, total, pct, quiz }: { done: number; total: number; pct: number; quiz: number | null }): ReactNode {
  return (
    <span className="tr-teamcell">
      <span className="tr-bar"><i style={{ width: `${pct}%` }} /></span>
      <span>{done}/{total}{quiz != null ? `, quiz ${quiz}%` : ""}</span>
    </span>
  );
}
